import React from 'react';
import ReactGA from 'react-ga4';
import './Integrations.css';

const INTEGRATION_LOGOS = [
  { name: 'Amazon Web Services', src: '/images/logo/aws.png' },
  { name: 'Microsoft Azure', src: '/images/logo/azure.svg' },
  { name: 'Google Cloud', src: '/images/logo/gcp.png' },
  { name: 'GitHub', src: '/images/logo/github.png' },
  {
    name: 'GitLab',
    src: '/images/logo/gitlab.svg',
    imgClassName: 'integration-tile__img--gitlab'
  },
  { name: 'Bitbucket', src: '/images/logo/bitbucket.svg' },
  {
    name: 'Wiz',
    src: '/images/logo/wiz.png',
    imgClassName: 'integration-tile__img--wiz'
  },
  { name: 'Snyk', src: '/images/logo/snyk.png' },
  { name: 'Prowler', src: '/images/logo/prowler.png' },
  { name: 'Orca Security', src: '/images/logo/orca.png' },
  { name: 'Upwind', src: '/images/logo/upwind.svg' },
  { name: 'Lacework FortiCNAPP', src: '/images/logo/forticnapp.png' },
  { name: 'Tenable', src: '/images/logo/tenable.png' },
  { name: 'SentinelOne', src: '/images/logo/sentinelone.svg' },
  {
    name: 'Trivy',
    src: '/images/logo/trivy.png',
    imgClassName: 'integration-tile__img--trivy'
  },
  { name: 'CrowdStrike', src: '/images/logo/crowdstrike.png' },
  { name: 'Palo Alto Networks', src: '/images/logo/palo-alto-networks.svg' },
  { name: 'Jira', src: '/images/logo/jira.svg' },
  { name: 'Confluence', src: '/images/logo/confluence.svg' },
  { name: 'Slack', src: '/images/logo/slack.png' }
];

const ROW_SPLIT_INDEX = Math.ceil(INTEGRATION_LOGOS.length / 2);
const MARQUEE_ROWS = [
  INTEGRATION_LOGOS.slice(0, ROW_SPLIT_INDEX),
  INTEGRATION_LOGOS.slice(ROW_SPLIT_INDEX)
];

/**
 * A row repeats its logos until one copy is wider than the viewport; otherwise a gap
 * opens as the track scrolls. Tiles occupy about 100px each, so three passes over ten
 * logos covers a 3000px screen. Two copies of that then make the loop seamless.
 */
const PASSES_PER_COPY = 3;
const MARQUEE_COPIES = [0, 1];

const IntegrationTile = ({ item }) => (
  <div className="integration-tile" data-vendor={item.name}>
    <div className="integration-tile__inner">
      <img
        className={['integration-tile__img', item.imgClassName].filter(Boolean).join(' ')}
        src={item.src}
        alt=""
        width={56}
        height={56}
        loading="lazy"
        decoding="async"
      />
    </div>
  </div>
);

const Integrations = () => {
  const handleEarlyAccessClick = () => {
    ReactGA.event({
      category: 'user_engagement',
      action: 'early_access_click',
      label: 'integrations_section'
    });
  };

  return (
    <>
      <section
        className="security-banner"
        aria-labelledby="security-banner-heading"
      >
        <div className="security-banner__panel">
          <video
            className="security-banner__video"
            autoPlay
            loop
            muted
            playsInline
            aria-hidden="true"
          >
            <source src="/videos/gradient-texture.mp4" type="video/mp4" />
          </video>
          <div className="security-banner__panel-overlay" aria-hidden="true" />
          <div className="security-banner__content">
            <p className="security-banner__stat">
              <span className="security-banner__stat-value">95%</span>
              <span className="security-banner__stat-label">Reduction in security noise</span>
            </p>
            <div className="security-banner__copy">
              <h2 id="security-banner-heading" className="panel-title security-banner__heading">
                Increase productivity with remediation built into the DevSecOps workflow.
              </h2>
              <p className="panel-title__lede security-banner__lede">
                Protect your infrastructure and applications with a network of intelligent agents designed to
                autonomously verify reported risk, manage complex prioritization, and remediation tasks at scale.
              </p>
              <div className="security-banner__actions">
                <a
                  href="#contact"
                  className="btn btn-gradient security-banner__cta"
                  onClick={handleEarlyAccessClick}
                >
                  Register for Access
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section
        id="integrations"
        className="integrations"
        aria-labelledby="integrations-heading"
      >
        <div className="page-container">
          <div className="page-section__header page-section__header--split">
            <h2 id="integrations-heading">Connect Your Entire Cloud Ecosystem</h2>
            <p>
              Circumvent unifies data from across your environment: cloud providers, code repositories, security
              products, and business tools. If it’s part of your stack, we can integrate with it.
            </p>
          </div>
          <ul className="visually-hidden" aria-label="Technology partner logos">
            {INTEGRATION_LOGOS.map((item) => (
              <li key={item.name}>{item.name}</li>
            ))}
          </ul>
        </div>
        <div className="integrations-marquee" aria-hidden="true">
          {MARQUEE_ROWS.map((row, rowIndex) => (
            <div className="integrations-marquee__row" key={`marquee-row-${rowIndex}`}>
              <div
                className={[
                  'integrations-marquee__track',
                  rowIndex % 2 === 1 ? 'integrations-marquee__track--reverse' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {MARQUEE_COPIES.map((copyIndex) => (
                  <div
                    className={[
                      'integrations-marquee__group',
                      copyIndex === 1 ? 'integrations-marquee__group--clone' : ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    key={`marquee-copy-${copyIndex}`}
                  >
                    {Array.from({ length: PASSES_PER_COPY }).flatMap((_, pass) =>
                      row.map((item) => (
                        <IntegrationTile item={item} key={`${item.name}-${copyIndex}-${pass}`} />
                      ))
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default Integrations;
